import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  addDoc,
  doc,
  onSnapshot,
  updateDoc,
  setDoc,
  getDoc,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { BAREMES, computeMontant, evaluerPlausibilite, PLAFONDS, PLAFOND_NOMBRE_PERSONNES } from "../data/baremes";
import { departementFromCodePostal } from "../utils/departementFromCodePostal";
import { compressImage } from "../utils/compressImage";
import { uploadPhotoToDrive, prechaufferEnvoiPhotos } from "../utils/uploadPhotoToDrive";
import { useCompteurAnime } from "../utils/useCompteurAnime";
import Confetti from "./Confetti";
import {
  Activity,
  Dumbbell,
  Pencil,
  ListChecks,
  Puzzle,
  Palette,
  Users,
  MapPin,
  Plus,
  Upload,
  Send,
  User,
  Trophy,
  Briefcase,
  Hospital,
  Heart,
  Camera,
  CheckCircle2,
  Download,
  Share2,
  XCircle,
  Trash2,
} from "lucide-react";
import { genererCarteParticipation } from "../utils/carteParticipation";
import andarLogo from "../assets/andar-logo.png";
import badgeLogo from "../assets/polyartrottons-badge.png";
import heroBanner from "../assets/hero-banner.jpg";
import closingIllustration from "../assets/closing-illustration.jpg";

// Icône et couleur d'accent par type d'action, reprises telles quelles du
// design system fourni (design-reference/Polyar Trottons - Participation.dc.html).
const TYPE_META = {
  marche: { Icon: Activity, accent: "magenta" },
  fitness: { Icon: Dumbbell, accent: "blue" },
  dictee: { Icon: Pencil, accent: "teal" },
  qcm: { Icon: ListChecks, accent: "orange" },
  enigme: { Icon: Puzzle, accent: "magenta" },
  dessin: { Icon: Palette, accent: "blue" },
  autre: { Icon: Users, accent: "teal" },
};

function accentVars(accent) {
  return {
    main: `var(--andar-${accent})`,
    tint: `var(--andar-${accent}-tint)`,
    deep: `var(--andar-${accent}-deep)`,
  };
}

function formatNombreFr(n) {
  return Math.round(n).toLocaleString("fr-FR");
}

function formatEuroFr(n) {
  const arrondi = Math.round(n * 100) / 100;
  return (
    arrondi.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
  );
}

// Cadre de participation demandé par la direction : grand public, club
// (nom à préciser), entreprise (Challenge entreprise - Défis Pro) ou
// service hospitalier ou de soins (Challenge services hospitaliers).
const CATEGORIES = {
  grand_public: { label: "Grand public", Icon: User, accent: "blue" },
  club: { label: "Club", Icon: Trophy, accent: "teal" },
  entreprise: { label: "Challenge entreprise (Défis Pro)", Icon: Briefcase, accent: "orange" },
  hopital: { label: "Challenge services hospitaliers (de soins)", Icon: Hospital, accent: "magenta" },
};

function suffixeOrdinal(n) {
  return Math.round(n) === 1 ? "er" : "ᵉ";
}

let nextActionId = 1;
const MAX_PHOTOS = 5;

// Liste de tous les pays (sauf la France), noms en français, triés.
// Les DOM-TOM ont un code postal français : ils restent dans le cas « France ».
const CODES_PAYS = "AD AE AF AG AI AL AM AO AR AS AT AU AW AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ CA CD CF CG CH CI CK CL CM CN CO CR CU CV CW CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FK FM FO GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW".split(" ");
const PAYS = (() => {
  let noms;
  try {
    const d = new Intl.DisplayNames(["fr"], { type: "region" });
    noms = CODES_PAYS.map((c) => d.of(c) || c);
  } catch (e) {
    noms = CODES_PAYS;
  }
  return [...new Set(noms)].sort((a, b) => a.localeCompare(b, "fr"));
})();
// Champ fichier invisible mais toujours "présent" (un display:none empêche
// parfois l'ouverture de l'appareil photo sur certains téléphones).
const STYLE_INPUT_CACHE = { position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" };
const estEcranTactile = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
const TAILLE_MAX_FICHIER = 10 * 1024 * 1024; // 10 Mo

// Nom de fichier lisible sur le Drive : 2026-10-03_Dupont_Marie_1.jpg
function nomFichierPhoto(nom, prenom, index, extension) {
  const propre = (t) =>
    String(t || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Za-z0-9-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "inconnu";
  const d = new Date();
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `${date}_${propre(nom)}_${propre(prenom)}_${index}.${extension}`;
}
const MAX_EMAILS = 10;
// La correction ou l'annulation se fait uniquement depuis l'écran de
// confirmation, juste après l'envoi (le jeton n'existe que dans la page).

function identifiantAleatoire() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function nouvelleAction() {
  return { id: nextActionId++, type: "marche", quantite: "" };
}

export default function ParticipantForm() {
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [codePostal, setCodePostal] = useState("");
  // Participation depuis l'étranger : pays (obligatoire) et ville (facultative)
  const [horsFrance, setHorsFrance] = useState(false);
  const [pays, setPays] = useState("");
  const [ville, setVille] = useState("");
  // Saisie libre (on peut effacer le "1" pour taper "2") ; la valeur utilisée
  // dans les calculs vaut au moins 1.
  // Champ vide par défaut : s'il n'est pas rempli, la déclaration compte
  // pour une personne.
  const [saisiePersonnes, setSaisiePersonnes] = useState("");
  const nombrePersonnes = Math.max(1, parseInt(saisiePersonnes, 10) || 1);
  const setNombrePersonnes = (n) => setSaisiePersonnes(n > 1 ? String(n) : "");
  const [categorie, setCategorie] = useState("grand_public");
  const [structure, setStructure] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [carte, setCarte] = useState(null); // { blob, url } de la carte à partager
  const [newsletter, setNewsletter] = useState(false);
  const [emails, setEmails] = useState([""]);
  // Déclaration en cours de modification : { docIds, jeton, rang, photoUrls, newsletterFait }
  const [modification, setModification] = useState(null);
  const [confirmationPrecedente, setConfirmationPrecedente] = useState(null);
  const [messageAnnulation, setMessageAnnulation] = useState("");
  const [actionEnCours, setActionEnCours] = useState(false);
  const [actions, setActions] = useState([nouvelleAction()]);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [etapeEnvoi, setEtapeEnvoi] = useState("");
  // Compression lancée dès le choix de la photo (en arrière-plan) : au moment
  // de l'envoi, les images sont déjà prêtes.
  const compressions = useRef(new Map());
  function compresserEnAvance(file) {
    if (!compressions.current.has(file)) {
      const estImage = (file.type || "").startsWith("image/");
      compressions.current.set(file, estImage ? compressImage(file).catch(() => file) : Promise.resolve(file));
    }
    return compressions.current.get(file);
  }

  // Après un envoi, on remonte en haut de la page une fois l'écran de
  // confirmation affiché (défilement instantané : le défilement "doux"
  // s'arrêtait n'importe où sur téléphone pendant que la page changeait).
  useEffect(() => {
    if (!confirmation) return;
    const remonter = () => window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const t1 = setTimeout(remonter, 30);
    const t2 = setTimeout(remonter, 450);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [confirmation]);
  const [resultMessage, setResultMessage] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [statsPubliques, setStatsPubliques] = useState({ nombreParticipations: null, totalMontant: null });

  const rangAnime = useCompteurAnime(
    statsPubliques.nombreParticipations !== null ? statsPubliques.nombreParticipations + 1 : null
  );
  const totalMontantAnime = useCompteurAnime(statsPubliques.totalMontant);

  // Compteur public : "nombreParticipations" est exact en temps réel
  // (incrémenté à chaque envoi), "totalMontant" est mis à jour toutes les
  // ~10 min par le script Apps Script une fois les participations validées.
  useEffect(() => {
    const ref = doc(db, "stats", "global");
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setStatsPubliques({
          nombreParticipations: data.nombreParticipations ?? 0,
          totalMontant: data.totalMontant ?? 0,
        });
      }
    });
    return unsubscribe;
  }, []);

  async function incrementerCompteurPublic() {
    const ref = doc(db, "stats", "global");
    try {
      await updateDoc(ref, { nombreParticipations: increment(1) });
    } catch (err) {
      await setDoc(ref, { nombreParticipations: increment(1) }, { merge: true });
    }
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data().nombreParticipations : null;
  }

  function updateAction(id, field, value) {
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, [field]: value } : a)));
  }

  function addAction() {
    setActions((prev) => [...prev, nouvelleAction()]);
  }

  function removeAction(id) {
    setActions((prev) => (prev.length > 1 ? prev.filter((a) => a.id !== id) : prev));
  }

  // Les fichiers choisis s'AJOUTENT à ceux déjà sélectionnés (utile sur
  // téléphone, où l'on ajoute souvent les photos une par une).
  function handleFileChange(e) {
    const nouveaux = Array.from(e.target.files || []);
    e.target.value = ""; // permet de choisir à nouveau le même fichier après l'avoir retiré
    const tropLourds = nouveaux.filter((f) => f.size > TAILLE_MAX_FICHIER);
    const acceptes = nouveaux.filter((f) => f.size <= TAILLE_MAX_FICHIER);
    const deja = new Set(photos.map((f) => f.name + "|" + f.size));
    const fusion = [...photos, ...acceptes.filter((f) => !deja.has(f.name + "|" + f.size))];
    let message = "";
    if (tropLourds.length > 0) {
      message = `${tropLourds.length > 1 ? "Ces fichiers dépassent" : "Ce fichier dépasse"} 10 Mo et n'a pas été ajouté : ${tropLourds.map((f) => f.name).join(", ")}.`;
    }
    if (fusion.length > MAX_PHOTOS) {
      message = (message ? message + " " : "") + `Vous pouvez joindre au maximum ${MAX_PHOTOS} photos par envoi : seules les ${MAX_PHOTOS} premières ont été gardées.`;
    }
    setError(message);
    setPhotos(fusion.slice(0, MAX_PHOTOS));
    if (fusion.length > 0) prechaufferEnvoiPhotos();
    fusion.slice(0, MAX_PHOTOS).forEach(compresserEnAvance);
  }

  function retirerPhoto(index) {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setError("");
  }

  // Génère la carte de participation dès que l'écran de confirmation s'affiche
  useEffect(() => {
    if (!confirmation) {
      setCarte(null);
      return undefined;
    }
    let annule = false;
    let url = null;
    genererCarteParticipation(confirmation)
      .then((blob) => {
        if (annule || !blob) return;
        url = URL.createObjectURL(blob);
        setCarte({ blob, url });
      })
      .catch((err) => console.warn("Carte de participation non générée :", err));
    return () => {
      annule = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [confirmation]);

  const peutPartager =
    typeof navigator !== "undefined" &&
    carte &&
    navigator.canShare &&
    navigator.canShare({ files: [new File([carte.blob], "polyartrottons.png", { type: "image/png" })] });

  const partagerCarte = async () => {
    if (!carte) return;
    const fichier = new File([carte.blob], "polyartrottons.png", { type: "image/png" });
    try {
      await navigator.share({
        files: [fichier],
        title: "Polyar'Trottons",
        // Le lien est mis dans le texte : avec une image jointe, plusieurs
        // applis (WhatsApp notamment) ignorent le champ "url" séparé.
        text:
          "J'ai relevé le défi Polyar'Trottons pour la recherche sur la polyarthrite rhumatoïde et les actions de l'ANDAR. " +
          "Et vous ? Toutes les infos sur https://www.polyartrottons.org et vos résultats se déclarent ici : " +
          window.location.origin,
      });
    } catch (err) {
      // partage annulé par la personne : rien à faire
    }
  };

  // Annule des participations grâce au jeton de l'envoi
  async function annulerDocuments(docIds, jeton) {
    for (const id of docIds) {
      await updateDoc(doc(db, "participations", id), {
        statut: "annule",
        jetonModification: jeton,
        updatedAt: serverTimestamp(),
      });
    }
  }

  async function annulerDeclaration() {
    if (!confirmation) return;
    if (!window.confirm("Annuler cette déclaration ? Elle ne sera plus comptée pour la recherche.")) return;
    setActionEnCours(true);
    try {
      await annulerDocuments(confirmation.docIds, confirmation.jeton);
      setConfirmation(null);
      setMessageAnnulation("Votre déclaration a bien été annulée. Vous pouvez en saisir une nouvelle ci-dessous.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      window.alert("L'annulation n'a pas pu être enregistrée. Réessayez dans un instant ou contactez l'ANDAR.");
    } finally {
      setActionEnCours(false);
    }
  }

  function modifierDeclaration() {
    if (!confirmation) return;
    const f = confirmation.formulaire;
    setPrenom(confirmation.prenom);
    setNom(f.nom);
    setCodePostal(f.codePostal);
    setHorsFrance(Boolean(f.pays));
    setPays(f.pays || "");
    setVille(f.ville || "");
    setNombrePersonnes(confirmation.nombrePersonnes);
    setCategorie(confirmation.categorie);
    setStructure(confirmation.structure);
    setActions(f.actions.map((a) => ({ ...nouvelleAction(), type: a.type, quantite: a.quantite })));
    setPhotos([]);
    setNewsletter(false);
    setEmails([""]);
    setModification({
      docIds: confirmation.docIds,
      jeton: confirmation.jeton,
      rang: confirmation.rang,
      photoUrls: f.photoUrls,
      newsletterFait: confirmation.newsletterFait,
      envoyeLe: confirmation.envoyeLe,
    });
    setConfirmationPrecedente(confirmation);
    setConfirmation(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function abandonnerModification() {
    setConfirmation(confirmationPrecedente);
    setModification(null);
    setConfirmationPrecedente(null);
    setActions([nouvelleAction()]);
    setPrenom("");
    setNom("");
    setCodePostal("");
    setHorsFrance(false);
    setPays("");
    setVille("");
    setNombrePersonnes(1);
    setCategorie("grand_public");
    setStructure("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setMessageAnnulation("");

    if (!prenom.trim() || !nom.trim()) {
      setError("Le prénom et le nom sont requis.");
      return;
    }
    if (categorie === "club" && !structure.trim()) {
      setError("Précisez le nom de votre club.");
      return;
    }
    if (categorie === "entreprise" && !structure.trim()) {
      setError("Précisez le nom de votre entreprise.");
      return;
    }
    if (categorie === "hopital" && !structure.trim()) {
      setError("Précisez le nom de votre service et de votre établissement.");
      return;
    }
    const emailsSaisis = [...new Set(emails.map((m) => m.trim().toLowerCase()).filter(Boolean))];
    const inscrireNewsletter = newsletter && !(modification && modification.newsletterFait);
    if (inscrireNewsletter && (emailsSaisis.length === 0 || emailsSaisis.some((m) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(m)))) {
      setError("Vérifiez les adresses email saisies pour la newsletter.");
      return;
    }

    if (horsFrance && !pays) {
      setError("Choisissez votre pays.");
      return;
    }
    // Hors de France, pas de code postal : la déclaration est rangée sous
    // « Étranger », avec le pays (et la ville) enregistrés à part.
    const departementTrouve = horsFrance
      ? { nom: "Étranger (" + pays + ")" }
      : departementFromCodePostal(codePostal.trim());
    if (!departementTrouve) {
      setError("Renseignez un code postal français valide (5 chiffres).");
      return;
    }

    const actionsValides = actions
      .map((a) => ({ ...a, quantite: parseFloat(a.quantite) }))
      .filter((a) => !isNaN(a.quantite) && a.quantite > 0);

    // Refus des valeurs manifestement impossibles (erreur de frappe, test fantaisiste)
    if (nombrePersonnes > PLAFOND_NOMBRE_PERSONNES) {
      setError(`Le nombre de participants semble trop élevé (maximum ${PLAFOND_NOMBRE_PERSONNES} par déclaration). Pour un très grand groupe, contactez l'ANDAR.`);
      return;
    }
    const tropGrande = actionsValides.find((a) => PLAFONDS[a.type] !== undefined && a.quantite > PLAFONDS[a.type]);
    if (tropGrande) {
      setError(`La quantité saisie pour « ${BAREMES[tropGrande.type].label} » semble trop élevée (maximum ${PLAFONDS[tropGrande.type].toLocaleString("fr-FR")} ${BAREMES[tropGrande.type].unit}). Vérifiez votre saisie.`);
      return;
    }

    if (actionsValides.length === 0) {
      setError("Indiquez au moins une quantité réalisée.");
      return;
    }

    setSubmitting(true);
    setResultMessage("Envoi de votre déclaration en cours…");

    try {
      const photoUrls = [];
      // Toutes les photos partent EN MÊME TEMPS (et non plus l'une après
      // l'autre) : l'attente totale est celle de la photo la plus lente.
      if (photos.length > 0) {
        let terminees = 0;
        setEtapeEnvoi(`Envoi des photos (0/${photos.length})…`);
        const envoyerUne = async (file, i) => {
            const estImage = (file.type || "").startsWith("image/");
            // Les images sont compressées en JPEG ; les PDF sont envoyés tels quels, avec leur vrai type.
            const contenu = await compresserEnAvance(file);
            const typeMime = estImage ? "image/jpeg" : file.type || "application/pdf";
            const extension = estImage ? "jpg" : (file.name.split(".").pop() || "pdf").toLowerCase();
            const url = await uploadPhotoToDrive(contenu, nomFichierPhoto(nom, prenom, i + 1, extension), typeMime);
            terminees += 1;
            setEtapeEnvoi(`Envoi des photos (${terminees}/${photos.length})…`);
            return url;
        };
        // Trois photos à la fois au maximum : rapide, sans saturer un réseau mobile.
        const urls = new Array(photos.length);
        let prochaine = 0;
        const ouvrier = async () => {
          while (prochaine < photos.length) {
            const i = prochaine++;
            urls[i] = await envoyerUne(photos[i], i);
          }
        };
        await Promise.all([ouvrier(), ouvrier(), ouvrier()]);
        photoUrls.push(...urls);
        setEtapeEnvoi("Enregistrement de votre participation…");
      }

      // Identifiant commun à toutes les performances de cet envoi : permet de
      // compter les personnes mobilisées une seule fois par envoi.
      const envoiId = identifiantAleatoire();
      // Jeton secret qui permet à l'auteur (et à lui seul) d'annuler cette
      // déclaration depuis l'écran de confirmation (voir firestore.rules).
      const jeton = identifiantAleatoire();

      // Modification : on annule d'abord l'ancienne déclaration
      if (modification) {
        await annulerDocuments(modification.docIds, modification.jeton);
        if (photoUrls.length === 0) photoUrls.push(...(modification.photoUrls || []));
      }
      const nouvellesEntrees = [];
      for (const action of actionsValides) {
        const montant = computeMontant(action.type, action.quantite, nombrePersonnes);
        // Les déclarations normales comptent tout de suite ("valide_auto").
        // Celles qui dépassent les seuils de contrôle sont mises en attente
        // ("a_valider") : elles n'entrent dans le total public qu'une fois
        // validées dans l'onglet "Contrôles" du Google Sheet.
        const { statut, motif } = evaluerPlausibilite(action.type, action.quantite, nombrePersonnes);
        const docRef = await addDoc(collection(db, "participations"), {
          prenom: prenom.trim(),
          nom: nom.trim(),
          codePostal: horsFrance ? "" : codePostal.trim(),
          departement: departementTrouve.nom,
          pays: horsFrance ? pays : "France",
          ville: horsFrance ? ville.trim() : "",
          nombrePersonnes,
          categorie,
          structure: categorie === "grand_public" ? "" : structure.trim(),
          type: action.type,
          quantiteDeclaree: action.quantite,
          montant,
          statut,
          motif,
          photoUrls,
          envoiId,
          jetonModification: jeton,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(), // lu par la synchronisation incrémentale
        });
        nouvellesEntrees.push({ id: docRef.id, type: action.type, quantite: action.quantite, montant, statut });
      }

      // Newsletter : une inscription par adresse. La première est celle du
      // déclarant ; les suivantes sont celles des autres membres du groupe.
      if (inscrireNewsletter) {
        for (let i = 0; i < emailsSaisis.length; i++) {
          await addDoc(collection(db, "newsletter_optins"), {
            prenom: i === 0 ? prenom.trim() : "",
            nom: i === 0 ? nom.trim() : "",
            email: emailsSaisis[i],
            codePostal: horsFrance ? "" : codePostal.trim(),
            departement: departementTrouve.nom,
            pays: horsFrance ? pays : "France",
            ajoutePar: i === 0 ? "" : `${prenom.trim()} ${nom.trim()}`,
            dateConsentement: serverTimestamp(),
          });
        }
      }

      // Compteur public non bloquant : les participations sont déjà
      // enregistrées à ce stade, un échec ici ne doit jamais afficher
      // un message d'erreur (sinon la personne renvoie et crée un doublon).
      // Une modification garde le rang d'origine (pas de nouveau déclarant)
      let rang = modification ? modification.rang : null;
      if (!modification) {
        try {
          rang = await incrementerCompteurPublic();
        } catch (errCompteur) {
          console.warn("Compteur public non mis à jour :", errCompteur);
        }
      }
      // Écran de confirmation : on fige le rang obtenu au moment de l'envoi,
      // pour qu'il ne bouge plus si d'autres personnes déclarent entre-temps.
      setConfirmation({
        prenom: prenom.trim(),
        rang,
        nombrePersonnes,
        categorie,
        structure: categorie === "grand_public" ? "" : structure.trim(),
        entrees: nouvellesEntrees,
        total: nouvellesEntrees.reduce((somme, e) => somme + e.montant, 0),
        // Pour pouvoir corriger ou annuler depuis l'écran de confirmation
        docIds: nouvellesEntrees.map((e) => e.id),
        jeton,
        envoyeLe: modification ? modification.envoyeLe : Date.now(),
        newsletterFait: Boolean(inscrireNewsletter || (modification && modification.newsletterFait)),
        formulaire: {
          nom: nom.trim(),
          codePostal: codePostal.trim(),
          pays: horsFrance ? pays : "",
          ville: horsFrance ? ville.trim() : "",
          actions: actionsValides.map((a) => ({ type: a.type, quantite: String(a.quantite) })),
          photoUrls,
        },
      });
      setModification(null);
      setConfirmationPrecedente(null);
      setResultMessage("");
      window.scrollTo({ top: 0, behavior: "smooth" });
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);

      setActions([nouvelleAction()]);
      setPhotos([]);
      setNewsletter(false);
      setEmails([""]);
      setPrenom("");
      setNom("");
      setCodePostal("");
      setHorsFrance(false);
      setPays("");
      setVille("");
      setNombrePersonnes(1);
      setCategorie("grand_public");
      setStructure("");
    } catch (err) {
      console.error(err);
      const detail = err && err.message ? String(err.message).slice(0, 220) : String(err);
      setError("Une erreur est survenue pendant l'envoi. Réessayez dans un instant. (Détail technique : " + detail + ")");
      setResultMessage("");
    } finally {
      setSubmitting(false);
      setEtapeEnvoi("");
    }
  }

  const contributionActuelle = actions.reduce((somme, a) => {
    const q = parseFloat(a.quantite);
    if (isNaN(q) || q <= 0) return somme;
    return somme + computeMontant(a.type, q, nombrePersonnes);
  }, 0);
  const nombreActionsRenseignees = actions.filter((a) => parseFloat(a.quantite) > 0).length;
  const statutTexte = resultMessage
    ? resultMessage
    : nombreActionsRenseignees > 0
    ? `${nombreActionsRenseignees} action(s) renseignée(s) sur ${actions.length}`
    : "Renseignez vos quantités pour voir votre contribution.";

  return (
    <div style={{ position: "relative", overflow: "hidden" }}>
      {showConfetti && <Confetti />}
      <div className="watermark" aria-hidden="true" />

      <div className="stripe-5" style={{ position: "relative", zIndex: 1 }}>
        <span style={{ background: "var(--andar-blue)" }} />
        <span style={{ background: "var(--andar-magenta)" }} />
        <span style={{ background: "var(--andar-teal)" }} />
        <span style={{ background: "var(--andar-yellow)" }} />
        <span style={{ background: "var(--andar-orange)" }} />
      </div>

      <div className="app-shell">
        <div className="app-header">
          <img src={andarLogo} alt="ANDAR" className="andar-logo" />
          <span style={{ flex: 1 }} />
          <img src={badgeLogo} alt="Polyar'Trottons 2026" className="badge-logo" />
        </div>

        <div className="hero-banner">
          <img src={heroBanner} alt="Polyar'Trottons — Chaque pas compte pour la recherche !" />
        </div>

        {confirmation ? (
          <div className="card">
            <div style={{ padding: "36px 28px 28px", textAlign: "center" }}>
              <CheckCircle2 size={48} color="var(--andar-teal-deep)" aria-hidden="true" />
              <h1 style={{ fontSize: 32, lineHeight: 1.1, margin: "14px 0 8px" }}>
                Merci {confirmation.prenom} !
              </h1>
              <p style={{ fontFamily: "var(--font-script)", fontSize: 22, lineHeight: 1.4, color: "var(--andar-magenta)", margin: "0 0 12px" }}>
                Vos résultats sont bien enregistrés
              </p>
              <p style={{ fontSize: 16, lineHeight: 1.6, margin: "0 auto", maxWidth: 520, color: "var(--text-body)" }}>
                Ils sont déjà comptés dans le total récolté pour la recherche. Notre équipe effectue des vérifications régulières.
              </p>
            </div>
            <div className="card-rule" style={{ background: "var(--andar-teal)" }} />

            <div style={{ padding: "24px 28px", display: "flex", flexWrap: "wrap", gap: 32, borderBottom: "1px solid var(--border-subtle)" }}>
              {confirmation.rang && (
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--andar-blue-deep)" }}>
                    Vous êtes le
                  </span>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 44, fontWeight: 700, lineHeight: 1, color: "var(--andar-blue-deep)", marginTop: 6 }}>
                    {formatNombreFr(confirmation.rang) + suffixeOrdinal(confirmation.rang)}
                  </div>
                  <p style={{ fontSize: 15, lineHeight: 1.5, color: "var(--text-body)", margin: "8px 0 0" }}>
                    participant à déclarer ses résultats.
                  </p>
                </div>
              )}
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--andar-magenta)" }}>
                  Ma contribution
                </span>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 44, fontWeight: 700, lineHeight: 1, color: "var(--andar-magenta-deep)", marginTop: 6 }}>
                  {formatEuroFr(confirmation.total)}
                </div>
                <p style={{ fontSize: 15, lineHeight: 1.5, color: "var(--text-body)", margin: "8px 0 0" }}>
                  pour la recherche contre la polyarthrite rhumatoïde.
                </p>
              </div>
            </div>

            <div style={{ padding: "20px 28px" }}>
              <h2 style={{ fontSize: 18, marginBottom: 10 }}>Récapitulatif</h2>
              {confirmation.entrees.map((e) => (
                <div
                  key={e.id}
                  style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border-subtle)", fontSize: 15 }}
                >
                  <span>
                    {BAREMES[e.type].label} : {formatNombreFr(e.quantite)} {BAREMES[e.type].unit}
                  </span>
                  <strong>{formatEuroFr(e.montant)}</strong>
                </div>
              ))}
            </div>

            <div style={{ padding: "8px 28px 24px", textAlign: "center", borderTop: "1px solid var(--border-subtle)" }}>
              <h2 style={{ fontSize: 20, margin: "20px 0 6px" }}>Partagez votre participation</h2>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: "var(--text-body)", margin: "0 auto 16px", maxWidth: 480 }}>
                Votre carte est prête : publiez-la sur vos réseaux pour inviter vos proches à relever le défi à leur tour.
              </p>
              {carte ? (
                <>
                  <img
                    src={carte.url}
                    alt="Votre carte de participation Polyar'Trottons"
                    style={{ width: "100%", maxWidth: 300, borderRadius: 16, boxShadow: "0 6px 24px rgba(0,0,0,.12)", display: "block", margin: "0 auto 16px" }}
                  />
                  <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                    <a
                      className="btn btn-primary"
                      href={carte.url}
                      download="polyartrottons-ma-participation.png"
                      style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
                    >
                      <Download size={18} style={{ verticalAlign: -3, marginRight: 8 }} aria-hidden="true" />
                      Télécharger
                    </a>
                    {peutPartager && (
                      <button type="button" className="btn btn-outline-secondary" onClick={partagerCarte} style={{ width: "auto", height: 52, padding: "0 28px", fontSize: 16 }}>
                        <Share2 size={18} style={{ verticalAlign: -3, marginRight: 8 }} aria-hidden="true" />
                        Partager
                      </button>
                    )}
                  </div>
                  <div style={{ marginTop: 18, padding: "16px 20px", borderRadius: "var(--radius-lg)", background: "var(--andar-magenta-tint)", textAlign: "center" }}>
                    <p style={{ fontSize: 14, margin: "0 0 12px", color: "var(--text-body)" }}>
                      Envie d'aller plus loin ? Vous pouvez aussi soutenir directement la recherche sur la polyarthrite rhumatoïde et les actions de l'ANDAR.
                    </p>
                    <a
                      href="https://www.polyarthrite-andar.org/catalog/faire-un-don"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10, width: "auto", height: 52, padding: "0 28px", fontSize: 16, textDecoration: "none" }}
                    >
                      <Heart size={20} aria-hidden="true" />
                      Faire un don
                    </a>
                  </div>
                </>
              ) : (
                <p style={{ fontSize: 14, color: "var(--text-muted)" }}>Préparation de votre carte…</p>
              )}
            </div>

            {confirmation.docIds && confirmation.docIds.length > 0 && (
              <div style={{ margin: "0 28px 20px", padding: "16px 20px", borderRadius: 16, background: "var(--surface-alt, #f6f6f8)", textAlign: "center" }}>
                <p style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>Une erreur dans votre déclaration ?</p>
                <p style={{ fontSize: 14, color: "var(--text-muted)", margin: "0 0 12px" }}>
                  Vous pouvez la corriger ou l'annuler tout de suite, depuis cet écran.
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <button type="button" className="btn btn-outline-secondary" onClick={modifierDeclaration} disabled={actionEnCours}
                    style={{ width: "auto", height: 44, padding: "0 20px", fontSize: 15 }}>
                    <Pencil size={16} style={{ verticalAlign: -3, marginRight: 6 }} aria-hidden="true" />
                    Modifier ma déclaration
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={annulerDeclaration} disabled={actionEnCours}
                    style={{ width: "auto", height: 44, padding: "0 20px", fontSize: 15, color: "var(--andar-magenta-deep, #9b0d4b)", borderColor: "currentColor" }}>
                    <XCircle size={16} style={{ verticalAlign: -3, marginRight: 6 }} aria-hidden="true" />
                    Annuler ma déclaration
                  </button>
                </div>
              </div>
            )}

            <div style={{ padding: "8px 28px 28px", textAlign: "center" }}>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmation(null)} style={{ width: "auto", height: 52, padding: "0 28px", fontSize: 16 }}>
                <Plus size={18} style={{ verticalAlign: -3, marginRight: 8 }} aria-hidden="true" />
                Déclarer d'autres résultats
              </button>
            </div>
          </div>
        ) : (
        <>
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ padding: "24px 28px", display: "flex", flexWrap: "wrap", gap: 32, alignItems: "center" }}>
            <div style={{ flex: "1 1 240px", minWidth: 0 }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "var(--ls-eyebrow)",
                  textTransform: "uppercase",
                  color: "var(--andar-blue-deep)",
                }}
              >
                Vous êtes le
              </span>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 44,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: "var(--andar-blue-deep)",
                  marginTop: 6,
                }}
              >
                {statsPubliques.nombreParticipations !== null ? formatNombreFr(rangAnime) + suffixeOrdinal(rangAnime) : "…"}
              </div>
              <p style={{ fontSize: 15, lineHeight: 1.5, color: "var(--text-body)", margin: "8px 0 0" }}>
                participant à déclarer ses résultats.
              </p>
            </div>
            <div style={{ flex: "1 1 260px", minWidth: 0 }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "var(--ls-eyebrow)",
                  textTransform: "uppercase",
                  color: "var(--andar-magenta)",
                }}
              >
                Total récolté
              </span>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 44,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: "var(--andar-magenta-deep)",
                  marginTop: 6,
                }}
              >
                {statsPubliques.totalMontant !== null ? formatEuroFr(totalMontantAnime) : "…"}
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text-muted)", margin: "10px 0 0" }}>
                grâce aux actions déclarées par les participants.
              </p>
            </div>
          </div>
          <div className="card-rule" style={{ background: "var(--andar-yellow)" }} />
        </div>

        <form className="card" onSubmit={handleSubmit}>
          {messageAnnulation && (
            <div role="status" style={{ margin: "20px 28px 0", padding: "14px 18px", borderRadius: 14, background: "var(--andar-teal-tint, #dcf3ef)", fontSize: 15 }}>
              {messageAnnulation}
            </div>
          )}
          {modification && (
            <div role="status" style={{ margin: "20px 28px 0", padding: "14px 18px", borderRadius: 14, background: "#fdecd6", fontSize: 15, display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
              <span>
                <strong>Vous modifiez votre déclaration.</strong> Corrigez les informations puis renvoyez-la : l'ancienne version sera remplacée.
                {modification.photoUrls && modification.photoUrls.length > 0 && " Vos photos sont conservées si vous n'en ajoutez pas de nouvelles."}
              </span>
              <button type="button" className="btn-text" onClick={abandonnerModification}>
                Abandonner la modification
              </button>
            </div>
          )}
          <div style={{ padding: "28px 28px 24px" }}>
            <h1 style={{ fontSize: 32, lineHeight: 1.1, marginBottom: 8 }}>Enregistrer mes résultats</h1>
            <p style={{ fontFamily: "var(--font-script)", fontSize: 22, lineHeight: 1.4, color: "var(--andar-magenta)", margin: "0 0 12px" }}>
              Chaque pas compte pour la recherche
            </p>
            <p style={{ fontSize: 17, lineHeight: 1.6, margin: 0, maxWidth: 620 }}>
              Déclarez vos kilomètres et vos actions : ils sont convertis en dons pour la recherche contre la
              polyarthrite rhumatoïde.
            </p>
          </div>
          <div className="card-rule" style={{ background: "var(--andar-magenta)" }} />

          <div
            style={{
              padding: 28,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 20,
              borderBottom: "1px solid var(--border-subtle)",
            }}
          >
            <div>
              <label htmlFor="prenom">Prénom *</label>
              <input id="prenom" type="text" required value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder="Marie" />
            </div>
            <div>
              <label htmlFor="nom">Nom *</label>
              <input id="nom" type="text" required value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Lecointre" />
            </div>
            {!horsFrance ? (
              <div>
                <label htmlFor="codePostal">Code postal</label>
                <div style={{ position: "relative" }}>
                  <MapPin
                    size={18}
                    style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }}
                    aria-hidden="true"
                  />
                  <input
                    id="codePostal"
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="75001"
                    value={codePostal}
                    onChange={(e) => setCodePostal(e.target.value.replace(/\D/g, "").slice(0, 5))}
                    style={{ paddingLeft: 44 }}
                  />
                </div>
                {codePostal.length === 5 && (
                  <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "6px 0 0" }}>
                    {departementFromCodePostal(codePostal)
                      ? `Département : ${departementFromCodePostal(codePostal).nom}`
                      : "Code postal non reconnu."}
                  </p>
                )}
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 400, marginTop: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={horsFrance} onChange={(e) => setHorsFrance(e.target.checked)} style={{ width: 16, height: 16 }} />
                  J'habite hors de France
                </label>
              </div>
            ) : (
              <div>
                <label htmlFor="pays">Pays *</label>
                <div style={{ position: "relative" }}>
                  <MapPin
                    size={18}
                    style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }}
                    aria-hidden="true"
                  />
                  <select
                    id="pays"
                    value={pays}
                    onChange={(e) => setPays(e.target.value)}
                    style={{ width: "100%", height: 52, padding: "0 16px 0 44px", borderRadius: "var(--radius-pill, 999px)", border: "1px solid var(--border-default, #d9d9d9)", background: "#fff", fontSize: 16, fontFamily: "inherit", color: "inherit" }}
                  >
                    <option value="">Choisissez un pays</option>
                    {PAYS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <input
                  id="ville"
                  type="text"
                  value={ville}
                  onChange={(e) => setVille(e.target.value)}
                  placeholder="Ville (facultatif)"
                  style={{ marginTop: 8 }}
                  aria-label="Ville (facultatif)"
                />
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 400, marginTop: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={horsFrance} onChange={(e) => setHorsFrance(e.target.checked)} style={{ width: 16, height: 16 }} />
                  J'habite hors de France
                </label>
              </div>
            )}
            <div>
              <label htmlFor="nombrePersonnes">Nombre de participants</label>
              <div style={{ position: "relative" }}>
                <Users
                  size={18}
                  style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }}
                  aria-hidden="true"
                />
                <input
                  id="nombrePersonnes"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  value={saisiePersonnes}
                  onChange={(e) => setSaisiePersonnes(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                  onBlur={() => { if (!(parseInt(saisiePersonnes, 10) >= 1)) setSaisiePersonnes(""); }}
                  style={{ paddingLeft: 44 }}
                />
              </div>
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "6px 0 0" }}>Personnes et animaux de compagnie. Laissez vide si vous êtes seul·e.</p>
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                id="newsletter"
                checked={newsletter}
                onChange={(e) => setNewsletter(e.target.checked)}
                style={{ width: "auto", height: "auto" }}
              />
              <label htmlFor="newsletter" style={{ margin: 0, fontWeight: 400 }}>
                Recevoir la newsletter de l'ANDAR
              </label>
            </div>
            {modification && modification.newsletterFait && (
              <p style={{ gridColumn: "1 / -1", fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
                Votre inscription à la newsletter est déjà enregistrée.
              </p>
            )}
            {newsletter && !(modification && modification.newsletterFait) && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label htmlFor="email-0">{emails.length > 1 ? "Adresses email" : "Adresse email"}</label>
                {emails.map((adresse, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    <input
                      id={`email-${i}`}
                      type="email"
                      placeholder={i === 0 ? "vous@email.fr" : "autre.personne@email.fr"}
                      value={adresse}
                      onChange={(e) => setEmails((prev) => prev.map((m, j) => (j === i ? e.target.value : m)))}
                    />
                    {i > 0 && (
                      <button
                        type="button"
                        className="btn-text"
                        aria-label={`Retirer l'adresse ${i + 1}`}
                        onClick={() => setEmails((prev) => prev.filter((_, j) => j !== i))}
                      >
                        <Trash2 size={18} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                ))}
                {emails.length < MAX_EMAILS && (
                  <button type="button" className="btn-text" onClick={() => setEmails((prev) => [...prev, ""])}>
                    <Plus size={16} style={{ verticalAlign: -3, marginRight: 4 }} aria-hidden="true" />
                    Ajouter l'adresse d'une autre personne du groupe
                  </button>
                )}
                <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "6px 0 0" }}>
                  Vous participez à plusieurs ? Ajoutez l'adresse de chaque personne qui souhaite recevoir la newsletter,
                  après avoir obtenu son accord. Chacune pourra se désinscrire à tout moment.
                </p>
              </div>
            )}
          </div>

          <div style={{ padding: 28, borderBottom: "1px solid var(--border-subtle)" }}>
            <h2 style={{ fontSize: 20, marginBottom: 14 }}>Je participe au titre de *</h2>
            <div role="radiogroup" aria-label="Je participe au titre de" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
              {Object.entries(CATEGORIES).map(([key, c]) => {
                const { main, tint, deep } = accentVars(c.accent);
                const selectionne = categorie === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={selectionne}
                    onClick={() => {
                      setCategorie(key);
                      setStructure("");
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      minHeight: 64,
                      padding: "12px 16px",
                      cursor: "pointer",
                      borderRadius: "var(--radius-lg)",
                      fontFamily: "var(--font-text)",
                      textAlign: "left",
                      transition: "background var(--dur-base) var(--ease-standard), border-color var(--dur-base) var(--ease-standard)",
                      border: selectionne ? `2px solid ${main}` : "2px solid var(--border-subtle)",
                      background: selectionne ? tint : "var(--surface-card)",
                      color: selectionne ? deep : "var(--text-body)",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: "var(--radius-pill)",
                        background: selectionne ? "var(--surface-card)" : tint,
                        flexShrink: 0,
                      }}
                    >
                      <c.Icon size={20} color={deep} aria-hidden="true" />
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>{c.label}</span>
                  </button>
                );
              })}
            </div>
            {categorie === "club" && (
              <div style={{ marginTop: 16, maxWidth: 420 }}>
                <label htmlFor="structure">Nom du club *</label>
                <input id="structure" type="text" value={structure} onChange={(e) => setStructure(e.target.value)} placeholder="Ex. : Randonneurs de la Loire" />
              </div>
            )}
            {categorie === "entreprise" && (
              <div style={{ marginTop: 16, maxWidth: 420 }}>
                <label htmlFor="structure">Nom de l'entreprise *</label>
                <input id="structure" type="text" value={structure} onChange={(e) => setStructure(e.target.value)} placeholder="Ex. : Société Martin" />
                <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "6px 0 0" }}>
                  Réservé aux entreprises inscrites au Challenge entreprise (Défis Pro).
                </p>
              </div>
            )}
            {categorie === "hopital" && (
              <div style={{ marginTop: 16, maxWidth: 420 }}>
                <label htmlFor="structure">Service et établissement *</label>
                <input id="structure" type="text" value={structure} onChange={(e) => setStructure(e.target.value)} placeholder="Ex. : Rhumatologie, CHU de Rennes" />
                <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "6px 0 0" }}>
                  Réservé aux services hospitaliers et de soins inscrits au challenge.
                </p>
              </div>
            )}
          </div>

          <div style={{ padding: 28 }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 12, marginBottom: 18 }}>
              <h2 style={{ fontSize: 20 }}>Mes performances réalisées</h2>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 14, color: "var(--text-muted)" }}>
                {actions.length === 1 ? "1 action" : `${actions.length} actions`}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {actions.map((action, index) => {
                const q = parseFloat(action.quantite);
                const contributionLigne =
                  !isNaN(q) && q > 0 ? `soit ${formatEuroFr(computeMontant(action.type, q, nombrePersonnes))} pour la recherche` : "";

                return (
                  <div
                    key={action.id}
                    style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-lg)", padding: 20, background: "var(--surface-subtle)" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                      <span style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-heading)" }}>
                        Action {index + 1}
                      </span>
                      <span style={{ flex: 1 }} />
                      {actions.length > 1 && (
                        <button type="button" className="btn-text" onClick={() => removeAction(action.id)}>
                          Retirer
                        </button>
                      )}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(128px, 1fr))", gap: 12 }}>
                      {Object.entries(BAREMES).map(([key, b]) => {
                        const { Icon, accent } = TYPE_META[key];
                        const { main, tint, deep } = accentVars(accent);
                        const selectionne = action.type === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => updateAction(action.id, "type", key)}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 8,
                              minHeight: 92,
                              padding: "14px 10px",
                              cursor: "pointer",
                              borderRadius: "var(--radius-lg)",
                              fontFamily: "var(--font-text)",
                              transition: "background var(--dur-base) var(--ease-standard), border-color var(--dur-base) var(--ease-standard)",
                              border: selectionne ? `2px solid ${main}` : "2px solid var(--border-subtle)",
                              background: selectionne ? tint : "var(--surface-card)",
                              color: selectionne ? deep : "var(--text-body)",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                borderRadius: "var(--radius-pill)",
                                background: selectionne ? "var(--surface-card)" : tint,
                                flexShrink: 0,
                              }}
                            >
                              <Icon size={22} color={deep} aria-hidden="true" />
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3, textAlign: "center" }}>{b.label}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16, marginTop: 18 }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <label>Quantité réalisée</label>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="0"
                            value={action.quantite}
                            onChange={(e) => updateAction(action.id, "quantite", e.target.value)}
                            style={{ flex: 1, minWidth: 0 }}
                          />
                          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text-muted)", minWidth: 62 }}>
                            {BAREMES[action.type].unit}
                          </span>
                        </div>
                      </div>
                      <div style={{ fontSize: 14, color: "var(--text-muted)", paddingBottom: 12 }}>{contributionLigne}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button type="button" className="btn btn-outline-secondary" onClick={addAction} style={{ marginTop: 16 }}>
              <Plus size={16} style={{ verticalAlign: -3, marginRight: 6 }} aria-hidden="true" />
              Ajouter une autre action
            </button>
          </div>

          <div style={{ padding: "0 28px 28px" }}>
            <div
              style={{
                border: "2px dashed var(--border-default)",
                background: "var(--andar-teal-tint)",
                borderRadius: "var(--radius-lg)",
                padding: "24px",
                textAlign: "center",
              }}
            >
              <Upload size={24} color="var(--andar-teal-deep)" aria-hidden="true" />
              <h3 style={{ fontSize: 16, margin: "10px 0 4px" }}>Ajouter des photos (facultatif)</h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 12px", maxWidth: 460, marginLeft: "auto", marginRight: "auto" }}>
                Jusqu'à {MAX_PHOTOS} photos (JPG, PNG ou PDF, 10 Mo maximum par fichier). Si une photo montre d'autres personnes
                que vous, assurez-vous de leur accord.
              </p>
              {photos.length < MAX_PHOTOS && (
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10 }}>
                  {estEcranTactile && (
                    <label className="btn btn-outline-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 8, width: "auto", height: 46, padding: "0 22px", fontSize: 15, cursor: "pointer", margin: 0, position: "relative" }}>
                      <Camera size={18} aria-hidden="true" />
                      Prendre une photo
                      <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} style={STYLE_INPUT_CACHE} />
                    </label>
                  )}
                  <label className="btn btn-outline-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 8, width: "auto", height: 46, padding: "0 22px", fontSize: 15, cursor: "pointer", margin: 0, position: "relative" }}>
                    <Plus size={18} aria-hidden="true" />
                    {estEcranTactile ? "Choisir dans la galerie" : photos.length === 0 ? "Choisir des photos" : "Ajouter une autre photo"}
                    <input type="file" accept="image/*,application/pdf" multiple onChange={handleFileChange} style={STYLE_INPUT_CACHE} />
                  </label>
                </div>
              )}
              {photos.length === 0 ? (
                <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "10px 0 0" }}>Aucune photo sélectionnée.</p>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: "14px auto 0", maxWidth: 460, textAlign: "left" }}>
                  {photos.map((f, i) => (
                    <li key={f.name + f.size} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--surface-card)", borderRadius: "var(--radius-pill)", padding: "6px 8px 6px 16px", marginTop: 6, fontSize: 13 }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                      <span style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>{(f.size / 1024 / 1024).toFixed(1).replace(".", ",")} Mo</span>
                      <button type="button" onClick={() => retirerPhoto(i)} aria-label={`Retirer ${f.name}`}
                        style={{ border: 0, background: "none", cursor: "pointer", padding: 4, display: "flex", color: "var(--andar-magenta)" }}>
                        <XCircle size={20} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                  <li style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8, textAlign: "center" }}>
                    {photos.length} / {MAX_PHOTOS} photo{photos.length > 1 ? "s" : ""}
                  </li>
                </ul>
              )}
            </div>
          </div>

          {error && <p className="error-message" style={{ padding: "0 28px" }}>{error}</p>}

          <div
            style={{
              padding: "20px 28px 28px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 20,
              borderTop: "1px solid var(--border-subtle)",
            }}
          >
            <div style={{ flex: "1 1 260px", minWidth: 0 }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "var(--ls-eyebrow)",
                  textTransform: "uppercase",
                  color: "var(--andar-magenta)",
                }}
              >
                Ma contribution
              </span>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 34, fontWeight: 700, color: "var(--andar-magenta-deep)", margin: "4px 0" }}>
                {formatEuroFr(contributionActuelle)}
              </div>
              <span style={{ fontSize: 15, color: "var(--text-muted)" }}>{statutTexte}</span>
            </div>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              <Send size={18} style={{ verticalAlign: -3, marginRight: 8 }} aria-hidden="true" />
              {submitting ? etapeEnvoi || "Envoi en cours…" : "Envoyer mes résultats"}
            </button>
          </div>
        </form>
        </>
        )}

        <div className="card" style={{ marginTop: 20, display: "flex", flexWrap: "wrap", alignItems: "center" }}>
          <img
            src={closingIllustration}
            alt="Marcheurs et participants du Polyar'Trottons"
            style={{ flex: "1 1 280px", minWidth: 0, width: "100%" }}
          />
          <div style={{ flex: "1 1 300px", minWidth: 0, padding: "24px 28px" }}>
            <p style={{ fontSize: 16, lineHeight: 1.7, margin: 0 }}>
              Marcher, courir, pédaler, résoudre une énigme : chacun de ces gestes soutient la recherche sur la
              polyarthrite rhumatoïde et les actions de l'ANDAR. Avançons ensemble.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
